import uniqid from 'uniqid';
import isFunction from '@utils/is-function';
import { screenLgMin, screenLgMax } from '@utils/match-media';
import FocusTrap from '@utils/focus-trap';
import keyCodes from '@utils/key-codes';
import requestAnimationFrame from '@utils/request-animation-frame';
import { toggleSelector, menuSelector } from './mega-nav';
import { mobileMegaNav } from './mobile-mega-nav';

const $body = $(document.body);
const $window = $(window);

const CLASS_NAME_ACTIVE_TOGGLE = '-active';
const CLASS_NAME_ACTIVE_MENU = '-active';

const EVENT_KEY = '_megaNavItem_';
const EVENT_CLICK = `click.${EVENT_KEY}`;
const EVENT_KEYDOWN = `keydown.${EVENT_KEY}`;
const EVENT_RESIZE = `resize.${EVENT_KEY};`

const FOCUS_TRAP_OPTIONS = {
    allowBreakout: true,
    preventFocusOrigin: true,
    nextKeys: [ keyCodes.ARROW_RIGHT ],
    prevKeys: [ keyCodes.ARROW_LEFT ]
}

export default class MegaNavItem {
    #active = false;
    #focusTrap = null;
    #toggleKey = null;

    constructor({ $toggle, $menu, matchHeight, transitionTime, topLevel }) {
        this.$toggle = $toggle;
        this.$menu = $menu;

        this.$parentMenu = this.$toggle.closest(menuSelector);
        this.$childToggles = this.$menu.find(toggleSelector);
        this.$childMenus = this.$menu.find(menuSelector);

        this.subscribers = {};

        this.id = uniqid.time('megaNavItem_');
        this.toggleId = `toggle_${this.id}`;

        this.EVENT_CLICK_INSTANCE = `${EVENT_CLICK}.${this.id}`;
        this.EVENT_KEYDOWN_INSTANCE = `${EVENT_KEYDOWN}.${this.id}`;
        this.EVENT_RESIZE_INSTANCE = `${EVENT_RESIZE}.${this.id}`;

        this.matchHeight = matchHeight || false;
        this.transitionTime = transitionTime || 0;
        this.topLevel = topLevel || false;

        this.init();
    }

    get active() {
        return this.#active;
    }

    set active(active) {
        this.#active = active;

        if(active) {
            this.$toggle.addClass(CLASS_NAME_ACTIVE_TOGGLE);
            this.$menu.addClass(CLASS_NAME_ACTIVE_MENU);

            this.toggleHeight();

            $body.on(this.EVENT_CLICK_INSTANCE, evt => this.onBodyClick(evt));
            $body.on(this.EVENT_KEYDOWN_INSTANCE, evt => this.onBodyKeydown(evt));

            this.afterTransition(() => {
                this.#focusTrap = new FocusTrap(this.$menu, {
                    ...FOCUS_TRAP_OPTIONS,
                    initialFocusIndex: this.#toggleKey === keyCodes.ARROW_UP ? -1 : 0,
                    onBreakout: (evt, focusTrap) => this.onBreakout(evt, focusTrap)
                });

                this.toggleTopLevel();
            });
        } else {
            this.$menu.removeAttr('style');

            this.#focusTrap = this.#focusTrap?.destroy();

            $body.off(this.EVENT_CLICK_INSTANCE);
            $body.off(this.EVENT_KEYDOWN_INSTANCE);
            $window.off(this.EVENT_RESIZE_INSTANCE);

            this.afterTransition(() => {
                this.$toggle.removeClass(CLASS_NAME_ACTIVE_TOGGLE)
                this.$menu.removeClass(CLASS_NAME_ACTIVE_MENU);
            });
        }

        this.setupAccessibility();
        this.#publish();
    }

    afterTransition(callback) {
        if(isFunction(callback)) {
            const duration = screenLgMax.matches ? this.transitionTime : 0;
            setTimeout(callback, duration);
        }
    }

    init() {
        this.setupAccessibility();
        this.setupListeners();
    }

    hide(focusOrigin = false) {
        if(focusOrigin) {
            this.$toggle.get(0).focus();
        }

        this.active = false;
    }

    onBodyClick(evt) {
        const $origin = $(evt.target);
        if($origin.data('modal') == true) {
            evt.preventDefault();
            $('#menu-modal').modal('show');
        }
        if(!$origin.closest(this.$menu).length && !$origin.closest(this.$toggle).length) {
            if(screenLgMax.matches) {
                mobileMegaNav.afterTransition(() => this.hide());
            } else {
                this.hide();
            }
        }
    }

    onBodyKeydown(evt) {
        const key = evt.which ? evt.which : evt.keyCode;
        if(key === keyCodes.ESCAPE) {
            const matches = this.#focusTrap.$focusable.filter((...[,el]) => el === evt.target );
            if(matches.length > 0) {
                this.hide(true);
            }
        }
    }

    onBreakout() {
        this.hide();
    }

    onToggleClick() {
        this.active = !this.active;
    }

    onToggleKeydown(evt) {
        this.#toggleKey = evt.which ? evt.which : evt.keyCode;
        if(this.#toggleKey === keyCodes.SPACE || this.#toggleKey === keyCodes.ARROW_DOWN || this.#toggleKey === keyCodes.ARROW_UP) {
            evt.preventDefault();

            this.show();
        }
    }

    onResize() {
        console.log(this, $window.outerHeight());
    }

    setupAccessibility() {
        this.$toggle.attr({
            'aria-controls': this.id,
            'aria-expanded': this.active,
            'id': this.toggleId
        });

        this.$menu.attr({
            'aria-hidden': !this.active,
            'aria-labelledby': this.toggleId,
            'id': this.id,
            'role': 'region'
        });
    }

    setupListeners() {
        this.$toggle.on(EVENT_CLICK, evt => this.onToggleClick(evt));
        this.$toggle.on(EVENT_KEYDOWN, evt => this.onToggleKeydown(evt));
    }

    show() {
        this.active = true;
    }

    subscribe(fn) {
        if(!isFunction(fn)) {
            return null;
        }

        const id = uniqid.time('subscriber_');
        this.subscribers[id] = fn;

        return function() {
            delete this.subscribers[id];
        }.bind(this); // need to bind scope to MegaNavItem instance for unsubscriber
    }

    toggleHeight() {
        if(this.matchHeight && screenLgMin.matches) {
            const menu = this.$menu.get(0);
            const height = menu.offsetHeight;
            const scrollHeight = menu.scrollHeight;
            const matchedHeight = Math.max(height, scrollHeight);
            if(matchedHeight > 1){
            this.$parentMenu.outerHeight(matchedHeight);
            }

            const updateMenuHeight = () => {
                const offsetY = menu.getBoundingClientRect().top;
                const viewportY = $window.outerHeight();
                this.$parentMenu.css('max-height', viewportY - offsetY);

                // add scrollbar height if applicable
                const scrollbarHeight = menu.offsetHeight - menu.clientHeight;
                if(matchedHeight > 1){
                    this.$parentMenu.outerHeight(matchedHeight + scrollbarHeight);
                }
                
            };

            updateMenuHeight();

            $window.on(this.EVENT_RESIZE_INSTANCE, () => requestAnimationFrame(() => updateMenuHeight()));
        }
    }

    toggleTopLevel() {
        if(this.topLevel && screenLgMin.matches) {
            this.$childToggles.eq(0).trigger('click');
        }
    }

    #publish() {
        Object.values(this.subscribers).forEach(fn => {
            fn(this); // pass this instance in sub function so parent has access
        });
    }
}
